import * as yup from 'yup';
import disposableDomains from 'disposable-email-domains';

const stringSchema = yup.string().trim();

const passwordSchema = stringSchema;

const getDomainFromEmail = (email: string) => {
  try {
    return email.split('@')[1].toLowerCase();
  } catch {
    return '';
  }
};

const EXTENDED_DISPOSABLE_DOMAINS = ['inbox.eu'];

const BLOCKED_PATTERNS = {
  PLUS_SIGN: {
    pattern: /\+/,
    message: 'Emails containing plus signs are not allowed'
  },
  COMPANY_NAME: {
    pattern: /sendsprint/i,
    message: 'Sendsprint email addresses are not allowed'
  }
};

const BLOCKED_KEYWORDS = {
  TEMP_EMAIL_KEYWORDS: ['inbox', 'mail', 'spam', 'temp', 'fake', 'disposable'],
  FINTECH_PROVIDERS: [
    'algorithm',
    'kyc',
    'send',
    'eversend',
    'transferwise',
    'worldremit',
    'stripe',
    'flutterwave',
    'paystack',
    'kuda',
    'opay',
    'carbon',
    'piggyvest',
    'cowrywise',
    'paga',
    'branch',
    'fairmoney',
    'alat',
    'eyowo',
    'chippercash',
    'paypal',
    'alipay',
    'venmo',
    'cashapp',
    'revolut',
    // 'n26',
    'monzo',
    'starling',
    'monese',
    'bunq',
    'tinkoff',
    'nubank',
    'neon',
    'picpay',
    'inter',
    'transfergo',
    'azimo',
    'remessa',
    'transfermate',
    'moneygram',
    'westernunion',
    'xoom',
    'riamoneytransfer',
    'smallworldfs',
    'worldfirst',
    'currencyfair',
    'wise',
    'crypto'
  ]
};

const enhancedEmailSchema = stringSchema
  .required('Please enter an email')
  .email('Please enter a valid email')
  .test('no-plus-sign', BLOCKED_PATTERNS.PLUS_SIGN.message, (value) => {
    if (!value) return true;

    const doesValueContainPlus = BLOCKED_PATTERNS.PLUS_SIGN.pattern.test(value);

    const [, domain] = value.split('@');
    const isDomainSendsprint = domain?.toLowerCase() === 'sendsprint.com';

    // Allow "sendsprint.com" domain
    if (isDomainSendsprint && doesValueContainPlus) {
      return true;
    }

    return !doesValueContainPlus;
  })
  .test('no-blocked-keywords', 'This email contains blocked keywords', (value) => {
    if (!value) return true;

    const [localPart, domain] = value.split('@');
    if (!localPart || !domain) return false;

    // Allow "sendsprint.com" domain
    if (domain.toLowerCase() === 'sendsprint.com') {
      return true;
    }

    const keywordList = BLOCKED_KEYWORDS.TEMP_EMAIL_KEYWORDS.concat(
      BLOCKED_KEYWORDS.FINTECH_PROVIDERS
    );

    const domainPrefix = domain.split('.')?.[0];
    const keywordRegex = new RegExp(`(${keywordList.join('|')})`, 'i');

    // Return false if either part contains blocked keyword
    const localFail = keywordRegex.test(localPart);
    const domainFail = keywordList.find(
      (item) => item.toLowerCase() === domainPrefix.toLowerCase()
    );

    return !(localFail || domainFail);
  })
  .test('no-disposable-email', 'Disposable email addresses are not allowed', (value) => {
    if (!value) return true;
    const domain = getDomainFromEmail(value);
    const _disposableDomains = [...disposableDomains, ...EXTENDED_DISPOSABLE_DOMAINS];
    return !_disposableDomains.includes(domain);
  });

const newPasswordSchema = passwordSchema
  // eslint-disable-next-line no-template-curly-in-string
  .min(8, 'Password must have a minimum of ${min} characters')
  .matches(/[0-9]+/, 'Password must have at least a number')
  .matches(/[a-zA-Z]+/, 'Password must have at least a letter');

const alphabetRegex = /^[A-Za-z]+$/;
const alphaNumericRegex = /^[A-Za-z0-9]+$/;
// const today = new Date();

export const userSchema = {
  firstName: stringSchema
    .required('Please enter a first name')
    .matches(alphabetRegex, 'Please enter only alphabets'),
  lastName: stringSchema
    .required('Please enter a last name')
    .matches(alphabetRegex, 'Please enter only alphabets'),
  email: enhancedEmailSchema,
  password: passwordSchema.required('Please enter a password'),
  registerPassword: newPasswordSchema.required('Please enter a password'),
  oldPassword: passwordSchema.required('Please enter your current password'),
  newPassword: newPasswordSchema.required('Please enter your new password'),
  referral: stringSchema
    .required('Please enter a referral code')
    .matches(alphaNumericRegex, 'Please enter either alphabets or numbers or both'),
  address: stringSchema.required('Please enter your address'),
  city: stringSchema.required('Please enter your city'),
  postalCode: stringSchema,
  country: stringSchema.required('Please select a country'),
  date: stringSchema.required('Please select your date of birth')
};

export const loginSchema = {
  email: stringSchema.required('Please enter an email'),
  password: userSchema.password
};

export const registerSchema = {
  firstName: userSchema.firstName,
  lastName: userSchema.lastName,
  email: userSchema.email,
  password: userSchema.registerPassword,
  referral: yup
    .string()
    .notRequired()
    .matches(alphaNumericRegex, 'Please enter either alphabets or numbers or both'),
  signUpSource: yup.string().notRequired(),
  acceptTerms: yup.boolean().oneOf([true], 'Kindly agree to the terms')
};

export const changePasswordSchema = {
  password: userSchema.registerPassword
};

export const resetPasswordSchema = {
  email: userSchema.email
};

const eighteenYearsAgo = new Date();
eighteenYearsAgo.setFullYear(eighteenYearsAgo.getFullYear() - 18);

export const addressSchema = {
  streetAddress: stringSchema.required('Please enter your street address'),
  postCode: stringSchema, // not required, because it can be different depending on country
  city: stringSchema.required('Please enter a city'),
  country: stringSchema.required('Please choose a country'),
  phone: stringSchema.required('Please enter your phone number'),
  state: stringSchema.required('Please select a state'),
  dateOfBirth: yup
    .date()
    .max(eighteenYearsAgo, 'You must be at least 18 years old')
    .required('Please add your date of birth')
    .test('valid-date', 'Invalid date format', (value) => {
      if (!value) return false;
      return !isNaN(value.getTime());
    })
    .test('no-future-date', 'Date of birth cannot be today or a future date', (value) => {
      if (!value) return false;
      const date = new Date(value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return date < today;
    }),
  twilioTerms: yup.boolean().oneOf([true], 'Kindly tick the box')
  // otp: stringSchema
  //   .required("Please enter the otp sent")
  //   .numbersOnly("Please enter only digits")
  //   .length(6, "Otp should be 6 digits"),
};
